import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { Lock, HeartHandshake, PhoneCall, ArrowRight, MapPin, Star } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Reveal, Stagger, StaggerItem, MaskedLine, SectionLabel } from "@/components/Reveal";
import SituationQuiz from "@/components/SituationQuiz";
import { situations, howWeHelp, faqs, stories, featuredStory, IMAGES, COUNTIES, PHONE_DISPLAY, PHONE_TEL, usePageTitle } from "@/lib/data";

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "-14%"]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden grain">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-0">
        <img src={IMAGES.texture} alt="" className="w-full h-[120%] object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-ivory/85 via-ivory/80 to-ivory" />
      </motion.div>

      <div className="relative z-[2] mx-auto max-w-7xl px-6 lg:px-12 w-full pt-36 pb-24 grid lg:grid-cols-12 gap-16 items-center">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="text-[11px] tracking-[0.3em] uppercase font-semibold text-sage-deep mb-8"
            data-testid="hero-eyebrow"
          >
            Property Resolutions Group · Central Florida
          </motion.p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.98] font-medium text-night" data-testid="hero-headline">
            <MaskedLine delay={0.25}>A calmer way through</MaskedLine>
            <MaskedLine delay={0.4}>
              <em className="not-italic text-sage-deep">difficult property</em>
            </MaskedLine>
            <MaskedLine delay={0.55}>moments.</MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.85, ease: [0.16, 1, 0.3, 1] }}
            className="mt-8 text-lg leading-relaxed text-charcoal max-w-xl"
            data-testid="hero-subtext"
          >
            If your home has become a source of stress — a short sale, foreclosure worries, an inherited
            property, liens, or title trouble — we help you understand your options privately, without
            pressure, and entirely on your timeline.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1, ease: [0.16, 1, 0.3, 1] }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/contact"
              data-testid="hero-primary-cta"
              className="rounded-full bg-night text-ivory px-8 py-4 text-sm tracking-wide hover:bg-sage-deep hover:scale-[1.03] transition-all duration-300 flex items-center gap-2"
            >
              Start a private conversation <ArrowRight size={15} strokeWidth={1.75} />
            </Link>
            <Link
              to="/services"
              data-testid="hero-secondary-cta"
              className="rounded-full border border-night/20 text-night px-8 py-4 text-sm tracking-wide hover:border-sage-deep hover:text-sage-deep transition-all duration-300"
            >
              See how we help
            </Link>
          </motion.div>
        </div>

        <motion.div
          style={{ y: imgY }}
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.3, delay: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-5 hidden lg:block"
        >
          <div className="relative rounded-[2.5rem] overflow-hidden shadow-[0_40px_90px_-30px_rgba(26,36,33,0.35)] rotate-2">
            <img src={IMAGES.passage} alt="A golden path through Florida oaks opening into warm light — there is a way forward" className="w-full h-[520px] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-night/25 to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 bg-ivory/85 backdrop-blur-md rounded-2xl px-6 py-4 flex items-center gap-3">
              <Lock size={15} strokeWidth={1.75} className="text-sage-deep shrink-0" />
              <p className="text-xs text-charcoal leading-snug">Every conversation is confidential. No signs, no pressure, no exceptions.</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

const Reassurance = () => {
  const items = [
    { icon: Lock, title: "Completely confidential", text: "Your situation stays between us. Always.", id: "reassurance-confidential" },
    { icon: HeartHandshake, title: "No pressure, ever", text: "We explain options. You decide what happens next.", id: "reassurance-no-pressure" },
    { icon: PhoneCall, title: "Reach us your way", text: "Call, text, or a private note — whatever feels easiest.", id: "reassurance-contact" },
  ];
  return (
    <section data-testid="reassurance-strip" className="border-y border-sage/20 bg-ivory relative z-[2]">
      <div className="mx-auto max-w-7xl px-6 lg:px-12 py-14 grid sm:grid-cols-3 gap-10">
        {items.map((it, i) => (
          <Reveal key={it.id} delay={i * 0.12}>
            <div data-testid={it.id} className="flex items-start gap-4">
              <it.icon size={22} strokeWidth={1.5} className="text-sage-deep mt-1 shrink-0" />
              <div>
                <p className="font-medium text-night">{it.title}</p>
                <p className="text-sm text-charcoal mt-1 leading-relaxed">{it.text}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
};

const Situations = () => {
  const spans = ["md:col-span-4", "md:col-span-2", "md:col-span-2", "md:col-span-4", "md:col-span-3", "md:col-span-3"];
  const bgs = ["bg-bone", "bg-coastal-pale", "bg-sage-pale", "bg-bone", "bg-coastal-pale", "bg-sage-pale"];
  return (
    <section data-testid="situations-section" className="py-28 lg:py-36">
      <div className="mx-auto max-w-7xl px-6 lg:px-12">
        <Reveal>
          <SectionLabel>Situations we help with</SectionLabel>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight text-night max-w-3xl">
            Whatever brought you here, you won't be rushed, judged, or sold to.
          </h2>
        </Reveal>
        <Stagger className="mt-16 grid grid-cols-1 md:grid-cols-6 gap-5">
          {situations.map((s, i) => (
            <StaggerItem key={s.id} className={spans[i]}>
              <Link
                to={`/services#${s.id}`}
                data-testid={`situation-card-${s.id}`}
                className={`group block h-full ${bgs[i]} rounded-3xl p-8 lg:p-10 hover:-translate-y-1.5 hover:shadow-[0_24px_60px_-20px_rgba(26,36,33,0.18)] transition-all duration-500`}
              >
                <h3 className="font-serif text-2xl lg:text-3xl text-night group-hover:text-sage-deep transition-colors duration-300">{s.title}</h3>
                <p className="mt-4 text-charcoal leading-relaxed text-[15px]">{s.text}</p>
                <span className="mt-6 inline-flex items-center gap-2 text-xs tracking-[0.18em] uppercase font-semibold text-sage-deep opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  How we help <ArrowRight size={13} strokeWidth={2} />
                </span>
              </Link>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>
  );
};

const EditorialMarquee = () => (
  <section data-testid="editorial-marquee" className="py-10 border-y border-sage/15 overflow-hidden" aria-hidden="true">
    <Marquee speed={28} gradient={false} pauseOnHover>
      {["Compassion", "Privacy", "Clarity", "No Pressure", "Local Roots", "Your Pace"].map((w) => (
        <span key={w} className="font-serif italic text-4xl sm:text-5xl text-sage/50 mx-10 flex items-center gap-20">
          {w} <span className="text-sand text-2xl not-italic">·</span>
        </span>
      ))}
    </Marquee>
  </section>
);

const HowWeHelp = () => (
  <section data-testid="how-we-help-section" className="py-28 lg:py-36 bg-bone grain">
    <div className="relative z-[2] mx-auto max-w-7xl px-6 lg:px-12">
      <Reveal>
        <SectionLabel>How we help</SectionLabel>
        <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight text-night max-w-2xl">
          Three gentle steps. Zero obligation.
        </h2>
      </Reveal>
      <div className="mt-20">
        {howWeHelp.map((step, i) => (
          <Reveal key={step.num} delay={i * 0.1}>
            <div data-testid={`how-step-${i + 1}`} className="grid md:grid-cols-12 gap-6 md:gap-10 py-12 border-t border-sage/25 items-start">
              <div className="md:col-span-3">
                <span className="font-serif text-6xl lg:text-8xl text-sage/60 leading-none">{step.num}</span>
              </div>
              <div className="md:col-span-4">
                <h3 className="font-serif text-2xl sm:text-3xl text-night">{step.title}</h3>
              </div>
              <div className="md:col-span-5">
                <p className="text-charcoal leading-relaxed">{step.text}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const LocalRoots = () => (
  <section data-testid="local-roots-section" className="py-28 lg:py-36">
    <div className="mx-auto max-w-7xl px-6 lg:px-12 grid lg:grid-cols-12 gap-14 items-center">
      <Reveal className="lg:col-span-6">
        <div className="rounded-[2.5rem] overflow-hidden -rotate-1 shadow-[0_40px_90px_-30px_rgba(26,36,33,0.3)]">
          <img src={IMAGES.shelter} alt="A sage-green Florida cottage glowing warm beneath a rainbow after the rain" className="w-full h-[440px] object-cover" />
        </div>
      </Reveal>
      <div className="lg:col-span-6">
        <Reveal>
          <SectionLabel>The heart of Central Florida</SectionLabel>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight text-night">
            We're your neighbors — not a call center.
          </h2>
          <p className="mt-6 text-charcoal leading-relaxed text-lg">
            PRG was built here, for homeowners here. From our home base in Winter Haven across Polk,
            Hillsborough, Hardee, and Highlands counties — Tampa included — we know the neighborhoods,
            the county courts, and the local title offices, and we treat every homeowner the way we'd
            want our own family treated.
          </p>
        </Reveal>
        <Stagger className="mt-8 flex flex-wrap gap-3">
          {COUNTIES.map((c) => (
            <StaggerItem key={c}>
              <span className="inline-flex items-center gap-2 rounded-full border border-sage/30 px-5 py-2 text-sm text-charcoal">
                <MapPin size={13} strokeWidth={1.75} className="text-sage-deep" /> {c} County
              </span>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </div>
  </section>
);

const Stories = () => (
  <section data-testid="stories-section" className="py-28 lg:py-36 bg-bone grain">
    <div className="relative z-[2] mx-auto max-w-7xl px-6 lg:px-12">
      <Reveal>
        <SectionLabel>Quiet wins</SectionLabel>
        <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight text-night max-w-3xl">
          Homeowners who found their way forward.
        </h2>
        <p className="mt-5 text-charcoal/70 text-sm italic">Shared with permission. Names withheld — privacy is the promise.</p>
      </Reveal>
      <Reveal className="mt-16">
        <figure data-testid="featured-story" className="bg-ivory rounded-[2rem] p-10 lg:p-14 shadow-[0_30px_70px_-25px_rgba(26,36,33,0.15)] relative overflow-hidden">
          <div className="absolute top-0 left-0 w-1.5 h-full bg-sage/60" />
          <div className="flex items-center gap-1.5" aria-label="Five star review">
            {[...Array(5)].map((_, i) => (
              <Star key={i} size={16} className="fill-sage-deep text-sage-deep" />
            ))}
          </div>
          <span className="mt-4 inline-block text-[10px] tracking-[0.2em] uppercase font-semibold text-coastal-deep">{featuredStory.tag}</span>
          <blockquote className="font-serif text-2xl sm:text-3xl lg:text-4xl text-night leading-snug mt-5 max-w-4xl">
            &ldquo;{featuredStory.quote}&rdquo;
          </blockquote>
          <figcaption className="mt-7 text-sm text-charcoal">— {featuredStory.who}</figcaption>
        </figure>
      </Reveal>
      <Stagger className="mt-6 grid md:grid-cols-2 gap-6">
        {stories.map((s, i) => (
          <StaggerItem key={s.tag}>
            <figure data-testid={`story-card-${i + 1}`} className="bg-ivory rounded-3xl p-9 h-full flex flex-col shadow-[0_20px_50px_-20px_rgba(26,36,33,0.1)]">
              <span className="text-[10px] tracking-[0.2em] uppercase font-semibold text-coastal-deep">{s.tag}</span>
              <blockquote className="font-serif text-xl sm:text-2xl text-night leading-snug mt-5 flex-1">
                &ldquo;{s.quote}&rdquo;
              </blockquote>
              <figcaption className="mt-7 text-sm text-charcoal">— {s.who}</figcaption>
            </figure>
          </StaggerItem>
        ))}
      </Stagger>
    </div>
  </section>
);

const FAQ = () => (
  <section data-testid="faq-section" className="py-28 lg:py-36 bg-ivory">
    <div className="mx-auto max-w-4xl px-6 lg:px-12">
      <Reveal className="text-center">
        <SectionLabel>Questions homeowners ask</SectionLabel>
        <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight text-night">
          Asked quietly, answered honestly.
        </h2>
      </Reveal>
      <Reveal className="mt-14" delay={0.15}>
        <Accordion type="single" collapsible data-testid="faq-accordion">
          {faqs.map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-item-${i + 1}`} className="border-b border-sage/25">
              <AccordionTrigger className="font-serif text-xl sm:text-2xl text-night text-left py-7 hover:text-sage-deep hover:no-underline transition-colors duration-300">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-charcoal leading-relaxed pb-7 text-base max-w-2xl">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>
    </div>
  </section>
);

const CTA = () => (
  <section data-testid="cta-section" className="bg-night text-ivory py-28 lg:py-36 grain">
    <div className="relative z-[2] mx-auto max-w-4xl px-6 lg:px-12 text-center">
      <Reveal>
        <SectionLabel light>When you're ready</SectionLabel>
        <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
          You don't have to figure this out alone.
        </h2>
        <p className="mt-6 text-ivory/70 leading-relaxed text-lg max-w-xl mx-auto">
          One short, private conversation is all it takes to understand your options.
          No cost, no obligation, no pressure.
        </p>
        <div className="mt-10 flex flex-wrap justify-center gap-4">
          <Link
            to="/contact"
            data-testid="cta-form-button"
            className="rounded-full bg-ivory text-night px-8 py-4 text-sm tracking-wide hover:bg-sage-pale hover:scale-[1.03] transition-all duration-300"
          >
            Send a private note
          </Link>
          <a
            href={PHONE_TEL}
            data-testid="cta-call-button"
            className="rounded-full border border-ivory/30 text-ivory px-8 py-4 text-sm tracking-wide hover:border-sage hover:text-sage transition-all duration-300"
          >
            Call {PHONE_DISPLAY}
          </a>
        </div>
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  usePageTitle("");
  return (
    <>
      <Hero />
      <Reassurance />
      <Situations />
      <EditorialMarquee />
      <HowWeHelp />
      <LocalRoots />
      <Stories />
      <section data-testid="fresh-start-section" className="py-28 lg:py-36">
        <div className="mx-auto max-w-7xl px-6 lg:px-12 grid lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <Reveal>
              <SectionLabel>Starting over</SectionLabel>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight text-night">
                Letting go of a house is not the end of the story.
              </h2>
              <p className="mt-6 text-charcoal leading-relaxed text-lg">
                For many of the families we help, the hardest part isn't the paperwork — it's the
                feeling that selling, settling, or walking away somehow means failing. It doesn't.
                A fresh start is a beginning, not a defeat.
              </p>
              <p className="mt-5 font-serif italic text-2xl text-sage-deep leading-snug">
                New roots take hold faster than you'd think — and we'd be honored to help you plant them.
              </p>
            </Reveal>
          </div>
          <Reveal className="lg:col-span-6 order-1 lg:order-2">
            <div className="rounded-[2.5rem] overflow-hidden rotate-1 shadow-[0_40px_90px_-30px_rgba(26,36,33,0.3)]">
              <img src={IMAGES.roots} alt="Caring hands holding a young sapling with fresh roots — a new beginning, carefully tended" className="w-full h-[440px] object-cover" />
            </div>
          </Reveal>
        </div>
      </section>
      <FAQ />
      <SituationQuiz />
      <CTA />
    </>
  );
}
